import { useState } from 'react';
import Textarea from '@shared/components/Textarea';
import { useDirtyTracker } from '@shared';
import { innerCard } from '@shared/lib/modalClasses';
import AdminSaveButton from '@/components/AdminSaveButton';
import { fmtDateTime } from '@/lib/alarmLabels';
import { addAlarmNote, type MonitorAlarm, type MonitorAlarmNote } from '@/services/api';

/** El mismo tope que el backend (`NOTE_MAX_CHARS`). */
const NOTE_MAX_CHARS = 2000;

/**
 * Las notas de una alarma (de la más nueva a la más vieja) y el cuadro para
 * añadir otra. Una nota no se edita ni se borra: es el diario de quién hizo qué.
 */
export default function AlarmNotes({
  alarmId,
  notes,
  canAdd,
  onAdded,
}: {
  alarmId: number;
  notes: MonitorAlarmNote[];
  canAdd: boolean;
  onAdded: (alarm: MonitorAlarm, note: MonitorAlarmNote) => void;
}) {
  const [text, setText] = useState('');
  const { isDirty, reset } = useDirtyTracker(text);

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed) throw new Error('La nota está vacía');
    const res = await addAlarmNote(alarmId, trimmed);
    onAdded(res.alarm, res.note);
    setText('');
    // `markClean` fijaría la línea base en el texto recién enviado (el valor del
    // render en curso); la nueva base es el cuadro vacío.
    reset('');
  };

  return (
    <div className="space-y-3">
      {canAdd ? (
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Qué se ha mirado, qué falta, a quién se ha avisado…"
            rows={3}
            maxLength={NOTE_MAX_CHARS}
            aria-label="Nueva nota"
          />
          <div className="flex justify-end">
            <AdminSaveButton onSave={save} dirty={isDirty && !!text.trim()} saveLabel="Añadir nota" savedLabel="Añadida" />
          </div>
        </div>
      ) : null}
      {notes.length === 0 ? (
        <p className="text-sm text-text-sub dark:text-slate-400">Sin notas.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n, i) => (
            <li key={`${n.at}-${i}`} className={innerCard}>
              <p className="text-[11px] text-text-sub dark:text-slate-400">
                {n.user_label || 'Sistema'}
                {n.via === 'ntfy' ? ' · desde ntfy' : ''} · {fmtDateTime(n.at)}
              </p>
              <p className="text-sm text-text-main dark:text-white whitespace-pre-wrap break-words">{n.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
